import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { ROOMS, TOWN_LOCATIONS } from '../rooms';

// VenuePlacer — the footprint brush.
//
// Moving a venue used to mean guessing: pick a building by eye, edit rooms.js,
// reload, walk in, discover the shop is three times the size of the block it
// landed on. The brush answers the question BEFORE the click — it draws the
// venue's real footprint, at true scale, wherever the pointer is, so "will this
// fit" is something you can see rather than something you find out.
//
// It draws the interior's measured size (worldStore.venueFootprints, recorded
// the first time anyone walks in) rather than a generic marker, because a
// generic marker is exactly what made this a guess in the first place.
//
// Best used in build view: orthographic and straight down means the outline on
// screen IS the footprint on the ground, with no perspective lean to discount.

const Y = 0.7;                  // above the kerbs, below nothing that matters
const GHOST_OK = '#9dff7d';
const GHOST_OVER = '#ff9d7d';

// Ground-plane hit for a pointer event. A MATHEMATICAL plane, not geometry:
// forecourts, kerbs and lots all stand above y=0, so a ray that has to hit a
// real surface stops on whatever is already there and reports its height
// instead of the ground under it. This is the same reason BlockGrid works this
// way, and the same bug it fixed.
// The city model's root, found once and remembered. The buildings are what the
// probe is asking about, and raycasting them alone is a fraction of the cost of
// walking the whole scene — which also contains the skyline rings, the ground
// disc and every overlay this tool draws itself.
let _cityRoot = null;
function cityRoot(scene) {
  if (_cityRoot?.parent) return _cityRoot;      // still mounted
  _cityRoot = null;
  scene.traverse((o) => {
    if (_cityRoot || !o.userData?.isCityModel) return;
    _cityRoot = o;
  });
  return _cityRoot;
}

function groundAt(ev, gl, camera, ray, plane, out, ndc) {
  const r = gl.domElement.getBoundingClientRect();
  ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
  ray.setFromCamera(ndc, camera);
  return ray.ray.intersectPlane(plane, out) ? out : null;
}

export default function VenuePlacer() {
  const ON = useWorldStore((s) => s.adminTool) === 'venues';
  const room = useWorldStore((s) => s.venuePlaceRoom);
  const sizes = useWorldStore((s) => s.venueFootprints);
  const draft = useWorldStore((s) => s.venuePlaceDraft);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const hover = useWorldStore((s) => s.venuePlaceHover);
  const setHover = useWorldStore((s) => s.setVenuePlaceHover);
  const setDraft = useWorldStore((s) => s.setVenuePlaceDraft);
  const { gl, camera, scene } = useThree();

  const ray = useMemo(() => new THREE.Raycaster(), []);
  const plane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  const hit = useMemo(() => new THREE.Vector3(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);
  const down = useRef(null);
  const lastProbe = useRef(0);

  // Latest values for the DOM listeners, which are bound once per tool session
  // and would otherwise close over the first render's brush forever.
  const live = useRef({});
  // The slider has to be in here too: the fit verdict compares the venue at its
  // CURRENT size against what is under the cursor, and a verdict computed off
  // the base size would keep saying OVER after you had already scaled it down.
  live.current = { room, sizes, setHover, setDraft, scale: draft[room]?.scale ?? overrides?.[room]?.scale ?? 1 };

  useEffect(() => {
    if (!ON) { setHover(null); return undefined; }
    const el = gl.domElement;

    const onMove = (ev) => {
      const p = groundAt(ev, gl, camera, ray, plane, hit, ndc);
      if (!p) return;
      const { room: r, sizes: sz } = live.current;

      // WHAT IS UNDER THE POINTER, throttled to ~12/s. A scene raycast is not
      // free and the answer only has to keep up with a hand, not with the frame
      // loop — the outline itself follows every single move, because that is
      // the part that has to feel attached to the cursor.
      let mesh = null; let meshSize = null;
      const now = performance.now();
      // PROBE RARELY, AND ONLY THE CITY.
      //
      // The first version raycast `scene.children` recursively at 12 Hz. That is
      // every object in the world — the whole city model, the skyline rings, the
      // ground, every marker and outline — walked and intersection-tested on the
      // main thread, several times a second, for as long as the pointer was
      // moving. On a scene this size that is tens of milliseconds a hit, and it
      // is why the machine started stuttering once this tool was open.
      //
      // `intersectObjects` also SORTS every hit by distance, when all that is
      // wanted is the nearest named thing. Confining the ray to the city model
      // and dropping to 6 Hz keeps the readout keeping up with a hand — which is
      // all it ever had to do — at a fraction of the cost. The outline itself
      // still follows every single pointer move; that is the part that has to
      // feel attached to the cursor, and it is pure arithmetic against a plane.
      if (now - lastProbe.current > 160) {
        lastProbe.current = now;
        ray.setFromCamera(ndc, camera);
        const root = cityRoot(scene);
        const hits = root ? ray.intersectObject(root, true) : [];
        for (const h of hits) {
          // Skip our own overlays and anything unnamed — a name is the whole
          // point, since that is what rooms.js `anchorMesh` takes.
          let o = h.object;
          while (o && !o.name) o = o.parent;
          if (!o || !o.name || /^(venue|ghost|grid)/i.test(o.name)) continue;
          mesh = o.name;
          const box = new THREE.Box3().setFromObject(o);
          if (isFinite(box.min.x)) {
            const s = new THREE.Vector3(); box.getSize(s);
            meshSize = [s.x, s.y, s.z];
          }
          break;
        }
      } else if (hover) {
        mesh = hover.mesh; meshSize = hover.meshSize;
      }

      const b = r ? sz?.[r] : null;
      const k = live.current.scale;
      const fp = b ? [b[0] * k, b[1] * k, b[2] * k] : null;
      live.current.setHover({
        x: +p.x.toFixed(1), z: +p.z.toFixed(1), mesh, meshSize,
        shop: fp ? [fp[0], fp[2]] : null,
        fits: fp && meshSize ? Math.max(fp[0] / meshSize[0], fp[2] / meshSize[2]) : null,
      });
    };

    const onDown = (ev) => { down.current = { x: ev.clientX, y: ev.clientY }; };
    const onClick = (ev) => {
      const { room: r } = live.current;
      if (!r) return;                       // no brush — nothing to drop
      // A pan ends in a click. Without this, dragging the map across to look at
      // another block would drop the venue wherever you let go.
      const d0 = down.current;
      if (d0 && Math.hypot(ev.clientX - d0.x, ev.clientY - d0.y) > 4) return;
      const p = groundAt(ev, gl, camera, ray, plane, hit, ndc);
      if (!p) return;
      live.current.setDraft(r, { x: +p.x.toFixed(2), z: +p.z.toFixed(2) });
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('click', onClick);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('click', onClick);
      setHover(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ON, gl, camera, scene]);

  if (!ON || !room) return null;
  const base = sizes?.[room];
  const loc = TOWN_LOCATIONS.find((l) => l.interiorId === room);
  const d = draft[room];
  const saved = overrides?.[room];
  // THE GHOST FOLLOWS THE CURSOR. Always, whenever the pointer is over the map.
  //
  // The previous version preferred a saved or drafted position, so that once a
  // venue had ever been placed the outline stopped moving — you could hover
  // anywhere and nothing responded, which made the whole tool look broken. It
  // traded the brush's ONE job (show me where this would go) for a much smaller
  // convenience (keep an outline visible while dragging the slider).
  //
  // The committed position is not this component's job: VenueFootprints draws
  // that ring and the marker sits on it, and both now follow the draft live. So
  // the ghost is free to be nothing but a preview, and only falls back to the
  // committed spot when the pointer is off the canvas entirely.
  const at = hover ? [hover.x, hover.z]
    : d?.x != null ? [d.x, d.z]
      : saved ? [saved.x, saved.z] : null;
  if (!at || !base) return null;

  // The brush draws BASE x SLIDER. The published footprint is deliberately the
  // base one (see MeshInterior), so the slider is applied here, once.
  const scale = d?.scale ?? saved?.scale ?? 1;
  const fp = [base[0] * scale, base[1] * scale, base[2] * scale];

  const over = hover?.fits;
  const color = over != null && over > 1.05 ? GHOST_OVER : (loc?.accent || GHOST_OK);
  const hw = fp[0] / 2;
  const hd = fp[2] / 2;
  // Same precedence as the room itself, so the ghost is a preview rather than a
  // second opinion — the stub line below is the only thing showing which way the
  // front faces, and it would be worse than useless pointing the wrong way.
  const rotY = d?.rotY ?? saved?.rotY ?? ROOMS[room]?.modelRotY ?? 0;

  return (
    <group position={[at[0], Y, at[1]]} rotation={[0, rotY, 0]} renderOrder={999}>
      {/* Filled, faintly — an outline alone disappears over a roof of the same
          colour, and the thing being judged is whether an AREA is clear. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[fp[0], fp[2]]} />
        <meshBasicMaterial color={color} transparent opacity={0.16} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
      </mesh>
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[new Float32Array([
              -hw, 0, -hd, hw, 0, -hd, hw, 0, hd, -hw, 0, hd, -hw, 0, -hd,
            ]), 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} transparent opacity={0.95} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
      </line>
      {/* Which way the shopfront points, so a rotated venue is not a mystery.
          Drawn from the centre to the −z face, which is the direction rooms.js
          `modelRotY` turns. */}
      <line>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[new Float32Array([0, 0, 0, 0, 0, -hd]), 3]} />
        </bufferGeometry>
        <lineBasicMaterial color={color} transparent opacity={0.7} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
      </line>
    </group>
  );
}
